import {
  useClassTypesQuery,
  useLevelsQuery,
} from "@/modules/master/presentation/api/queries";
import { CreatePackageTemplateSchema } from "@/modules/packages/domain/contracts";
import { config, isDevMode } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import {
  Button,
  FormInput,
  FormNumber,
  FormPrice,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { PlusIcon, SparklesIcon, TrashIcon } from "lucide-react";
import { useMemo } from "react";
import type { z } from "zod";
import { getRandomMockData, mockPackageTemplates } from "./dev-mock-data";

type ClassTypeLookupItem = { id: string; name: string };
type LevelLookupItem = { id: string; name: string };

const PackageTemplateFormSchema = CreatePackageTemplateSchema;
export type PackageTemplateFormValues = z.infer<
  typeof PackageTemplateFormSchema
>;

type PackageTemplateFormProps = {
  initialValues?: Partial<PackageTemplateFormValues>;
  onSubmit: (values: PackageTemplateFormValues) => void;
  submitting?: boolean;
};

export function PackageTemplateForm({
  initialValues,
  onSubmit,
  submitting,
}: PackageTemplateFormProps) {
  const { data: classTypesData } = useClassTypesQuery({ limit: 100 });
  const { data: levelsData } = useLevelsQuery({ limit: 100 });

  const firstActiveClassTypeId = useMemo(
    () => classTypesData?.data.find((ct) => ct.status === "active")?.id,
    [classTypesData],
  );

  const firstActiveLevelId = useMemo(
    () => levelsData?.data.find((l) => l.status === "active")?.id,
    [levelsData],
  );

  const methods = RHF.useForm<PackageTemplateFormValues>({
    resolver: zodResolver(PackageTemplateFormSchema),
    defaultValues: {
      name: initialValues?.name || "",
      totalSessions: initialValues?.totalSessions ?? undefined,
      validDays: initialValues?.validDays ?? undefined,
      price: initialValues?.price ?? undefined,
      description: initialValues?.description || "",
      maxSelectableClasses: initialValues?.maxSelectableClasses ?? undefined,
      status: initialValues?.status || "active",
      rules: initialValues?.rules || [],
    },
  });

  const rules = RHF.useFieldArray({
    control: methods.control,
    name: "rules",
  });

  const addRule = () => {
    rules.append({ classTypeId: "", levelId: "" });
  };

  const removeRule = (index: number) => {
    rules.remove(index);
  };

  const handleAutoFill = () => {
    const mockData = getRandomMockData(mockPackageTemplates);
    // Fill rules with first active class type and level if available
    const mockRules =
      firstActiveClassTypeId && firstActiveLevelId
        ? [{ classTypeId: firstActiveClassTypeId, levelId: firstActiveLevelId }]
        : [{ classTypeId: "", levelId: "" }];

    methods.reset({
      ...mockData,
      rules: mockRules,
    });
  };

  return (
    <FormRoot methods={methods} onSubmit={onSubmit} className="space-y-6">
      <div className="space-y-4">
        <FormInput
          name="name"
          label="ຊື່ແພັກເກດ"
          placeholder="ປ້ອນຊື່ແພັກເກດ"
          requiredMark
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormNumber
            name="totalSessions"
            label="ຈຳນວນຄັ້ງ"
            placeholder="ປ້ອນຈຳນວນຄັ້ງ"
            requiredMark
          />

          <FormNumber
            name="validDays"
            label="ຈຳນວນວັນ"
            placeholder="ປ້ອນຈຳນວນວັນ"
            requiredMark
          />

          <FormPrice
            name="price"
            label="ລາຄາ (ກີບ)"
            placeholder="ປ້ອນລາຄາ"
            requiredMark
            currencySymbol="₭"
          />
        </div>

        <FormTextarea
          name="description"
          label="ຄຳອະທິບາຍ"
          placeholder="ປ້ອນຄຳອະທິບາຍ (ບໍ່ບັງຄັບ)"
          rows={3}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormSelect
            label="ສະຖານະ"
            name="status"
            options={[
              { value: "active", label: "ໃຊ້ງານ" },
              { value: "inactive", label: "ບໍ່ໃຊ້ງານ" },
            ]}
          />

          {rules.fields.length > 0 && (
            <FormNumber
              name="maxSelectableClasses"
              label="ຈຳນວນຄລາດສ໌ທີ່ເລືອກໄດ້"
              placeholder="ບໍ່ຈຳກັດ (ວ່າງ)"
              min={1}
              max={rules.fields.length}
            />
          )}
        </div>
        {rules.fields.length > 0 && (
          <p className="-mt-2 text-muted-foreground text-xs">
            ຖ້າກຳນົດ ນັກຮຽນຈະເລືອກຄລາດສ໌ໄດ້ຕາມຈຳນວນນີ້ເວລາຂາຍ
          </p>
        )}

        {/* Allow Rules Section */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">ກົດລະບຽບທີ່ອະນຸຍາດ</h3>
            <p className="text-muted-foreground text-sm">
              ກຳນົດວ່າແພັກເກດນີ້ສາມາດໃຊ້ກັບຄລາດສ໌ປະເພດໃດ ແລະ ລະດັບໃດ
            </p>
          </div>
          <Button type="button" variant="outline" onClick={addRule}>
            <PlusIcon className="h-4 w-4" />
            ເພີ່ມກົດລະບຽບ
          </Button>
        </div>

        {rules.fields.length === 0 ? (
          <div className="rounded-lg border border-dashed p-4 text-center text-muted-foreground text-sm">
            ຍັງບໍ່ມີກົດລະບຽບ. ກະລຸນາເພີ່ມຢ່າງໜ້ອຍ 1 ກົດລະບຽບ.
          </div>
        ) : (
          <div className="space-y-3">
            {rules.fields.map((field, index) => (
              <div
                key={field.id}
                className="flex items-end gap-2 rounded-lg border p-3"
              >
                <div className="flex-1">
                  <FormInfiniteCombobox<ClassTypeLookupItem>
                    name={`rules.${index}.classTypeId`}
                    label="ປະເພດຄລາດສ໌"
                    requiredMark
                    queryKey={["master", "class-types", "lookup"]}
                    queryFn={(args) =>
                      fetchLookupForInfinite<ClassTypeLookupItem>(
                        `${config.apiUrl}/master/class-types/lookup`,
                        args,
                      )
                    }
                    preloadQueryFn={(id) =>
                      hydrateLookupItem<ClassTypeLookupItem>(
                        `${config.apiUrl}/master/class-types/lookup`,
                        id,
                      )
                    }
                    getLabel={(item) => item.name}
                    getValue={(item) => item.id}
                    placeholder="ເລືອກປະເພດຄລາດສ໌..."
                  />
                </div>
                <div className="flex-1">
                  <FormInfiniteCombobox<LevelLookupItem>
                    name={`rules.${index}.levelId`}
                    label="ລະດັບ"
                    requiredMark
                    queryKey={["master", "levels", "lookup"]}
                    queryFn={(args) =>
                      fetchLookupForInfinite<LevelLookupItem>(
                        `${config.apiUrl}/master/levels/lookup`,
                        args,
                      )
                    }
                    preloadQueryFn={(id) =>
                      hydrateLookupItem<LevelLookupItem>(
                        `${config.apiUrl}/master/levels/lookup`,
                        id,
                      )
                    }
                    getLabel={(item) => item.name}
                    getValue={(item) => item.id}
                    placeholder="ເລືອກລະດັບ..."
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeRule(index)}
                  className="mb-0"
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 border-t pt-4">
        {isDevMode && (
          <Button
            type="button"
            variant="outline"
            onClick={handleAutoFill}
            disabled={submitting}
          >
            <SparklesIcon className="size-4" />
            Auto Fill
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => methods.reset()}
          disabled={submitting}
        >
          ລ້າງ
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
        </Button>
      </div>
    </FormRoot>
  );
}
